import { PromotionEvaluation, SkippedPromotion } from '../../../../types/cart'

/**
 * Which refusal, if any, belongs to the code the booker entered.
 *
 * Pulled out of the component as a pure function because it is the subtlest logic in this
 * directory and was previously reachable only through a full render — every case had to be
 * set up as a mounted component with a mocked query, which is a lot of machinery for a
 * question that is really about one array.
 *
 * Returns undefined when the code APPLIED: there is nothing to explain, and the caller
 * shows the applied promotion instead.
 *
 * The three branches, in order:
 *
 *  1. The service's own mark. It resolved the code against a promotion in order to
 *     evaluate it, so it knows which entry is the booker's; nothing else does.
 *  2. A `Code`-prefixed reason — `CodeRequired`, `CodeInvalid`, `CodeAlreadyUsed`,
 *     `CodeAmbiguous`. The mark is absent when the code matched nothing, when it was
 *     ambiguous across providers, or when the response came from an older service —
 *     `false` means "not known to be yours", not "not yours" — so dropping to the guess is
 *     a degradation rather than a failure.
 *
 *     THIS PREFIX WAS `CODE_` AND MATCHED NOTHING. The wire enum is PascalCase, so the
 *     branch was dead for its entire life and every case that "covered" it asserted an
 *     invented value (`CODE_ALREADY_USED`, and a `CODE_NOT_FOUND` that is not a SkipReason
 *     in any casing). A code rejected as already-used therefore fell to branch 3 and the
 *     booker saw an unrelated promotion's sentence — the same symptom as the `CodeEntry`
 *     mismatch fixed alongside this, one branch further down. `reason` is now a union so
 *     the fixtures cannot drift back.
 *  3. First skipped entry. Nothing orders this array, so this is a guess and is the reason
 *     branch 1 exists at all.
 *
 * The fallback mirrors the php orchestrator's deliberately: two surfaces answering the same
 * question differently is how a booker gets one explanation here and another through a
 * partner.
 */
export function selectRefusal(
  result: PromotionEvaluation | undefined,
  codeApplied: boolean
): SkippedPromotion | undefined {
  if (codeApplied) {
    return undefined
  }

  return (
    result?.skipped.find((skip) => skip.matchedSuppliedCode) ??
    result?.skipped.find((skip) => skip.reason.startsWith('Code')) ??
    result?.skipped[0]
  )
}
